import { FC } from 'react';
import { LocalizedSectionTitle } from '@mews-commander/ui';
import { Button, IconName, Link, Stack } from '@optimus-web/core';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { WithAvailabilityBlockId } from '@mews-commander/types';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { useAppConfiguration } from '@mews-commander/data-access';
import { PlatformEnvironment } from '@mews/enums';
import AvailabilityBlockStateTag from '../../../../components/commander-ui/connected/AvailabilityBlockStateTag';
import { useAvailabilityBlockInfo } from '../../utils/useAvailabilityBlockInfo';
import { NotesButton } from './buttons/NotesButton';
import { BookingEngineButton } from './buttons/BookingEngineButton';

const useEmsBaseUrl = () => {
    const emsUrlMigrationEnabled = useFeatureFlag(FeatureFlag.emsUrlMigrationEnabled);

    return emsUrlMigrationEnabled
        ? {
              EMS_DEV: 'https://app.mews-develop.com/events/app',
              EMS_TEST: 'https://app.mews-test.com/events/app',
              EMS_PREPROD: 'https://app.mews-demo.com/events/app',
              EMS_PROD: 'https://app.mews.com/events/app',
          }
        : {
              EMS_DEV: 'https://quotelo-quotation-dev.azurewebsites.net',
              EMS_TEST: 'https://app.quotelotest.eu',
              EMS_PREPROD: 'https://app.quotelopreprod.eu',
              EMS_PROD: 'https://app.quotelo.io',
          };
};

const useConnectLink = (environmentToConnectTo: PlatformEnvironment) => {
    const { EMS_DEV, EMS_TEST, EMS_PREPROD, EMS_PROD } = useEmsBaseUrl();
    switch (environmentToConnectTo) {
        case PlatformEnvironment.Demo:
            return EMS_PREPROD;
        case PlatformEnvironment.Development:
            return EMS_TEST;
        case PlatformEnvironment.Feature:
            return EMS_DEV;
        case PlatformEnvironment.Production:
            return EMS_PROD;
        case PlatformEnvironment.Quality:
            return EMS_TEST;
        case PlatformEnvironment.Test:
            return EMS_TEST;
        default:
            return environmentToConnectTo satisfies never;
    }
};

type StatusBarProps = WithAvailabilityBlockId;

export const StatusBar: FC<StatusBarProps> = ({ availabilityBlockId }) => {
    const { availabilityBlock, isConfirmed } = useAvailabilityBlockInfo(availabilityBlockId);
    const { environment } = useAppConfiguration();

    const connectLink = useConnectLink(environment);

    if (!availabilityBlock) {
        return null;
    }

    return (
        <Stack alignment="center" distribution="space-between">
            <Stack spacing="200" itemAlignment="center">
                <LocalizedSectionTitle textKey={TextKey.Status} />
                <AvailabilityBlockStateTag availabilityBlockId={availabilityBlockId} />
            </Stack>

            <Stack spacing="200" itemAlignment="end">
                {isConfirmed && <BookingEngineButton availabilityBlock={availabilityBlock} />}

                <NotesButton availabilityBlock={availabilityBlock} />
                {availabilityBlock.quoteId ? (
                    <Button icon={IconName.Connect} type="button">
                        <Link
                            target="_blank"
                            rel="noreferrer"
                            href={`${connectLink}/quotes/${availabilityBlock.quoteId}`}
                            underline={false}
                            variant="basic"
                        >
                            <LocalizedText textKey={TextKey.Contract} />
                        </Link>
                    </Button>
                ) : null}
            </Stack>
        </Stack>
    );
};
